import { supabase } from './supabase';
import { logger } from '../utils/logger';
import { blockUser } from './connectionsService';

/**
 * Notify all admins about a new report
 */
async function notifyAdminsAboutNewReport(
  reportId: string,
  reportType: 'user' | 'group' | 'session',
  reason: string
): Promise<void> {
  try {
    // Get all admin users with push notifications enabled
    const { data: admins, error: adminsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('is_admin', true)
      .eq('push_enabled', true)
      .or('fcm_token.not.is.null,apns_token.not.is.null');

    if (adminsError) {
      logger.error('Error fetching admins:', adminsError);
      return;
    }

    if (!admins || admins.length === 0) {
      logger.log('No admins with push notifications enabled');
      return;
    }

    // Get current user ID for senderId
    const { data: { user } } = await supabase.auth.getUser();
    const senderId = user?.id || 'system';

    // Send notification to each admin
    const notificationPromises = admins.map(async (admin) => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              type: 'new_report',
              recipientId: admin.id,
              reportId: reportId,
              reportType: reportType,
              reportReason: reason,
              senderId: senderId,
            }),
          }
        );

        if (!response.ok) {
          logger.error(`Failed to send notification to admin ${admin.id}`);
        }
      } catch (error) {
        logger.error(`Error sending notification to admin ${admin.id}:`, error);
      }
    });

    // Wait for all notifications to be sent (but don't block on errors)
    await Promise.allSettled(notificationPromises);
    logger.log(`✅ Sent report notifications to ${admins.length} admin(s)`);
  } catch (error) {
    logger.error('Error in notifyAdminsAboutNewReport:', error);
    // Don't throw - notifications are best-effort
  }
}

export interface Report {
  id: string;
  reporter_id: string;
  reported_user_id?: string;
  reported_group_id?: string;
  reported_session_id?: string;
  report_type: 'user' | 'group' | 'session';
  reason: string;
  description?: string;
  created_at: string;
  status: 'pending' | 'reviewed' | 'resolved';
}

export const REPORT_REASONS = [
  'Inappropriate Content',
  'Harassment',
  'Spam',
  'Fake Profile',
  'Scam or Fraud',
  'Underage User',
  'Other'
] as const;

export const GROUP_REPORT_REASONS = [
  'Inappropriate Content',
  'Harassment',
  'Spam',
  'Hate Speech',
  'Illegal Activity',
  'Other'
] as const;

export const SESSION_REPORT_REASONS = [
  'Inappropriate Content',
  'Harassment',
  'Fake Session',
  'Dangerous Meeting',
  'Scam or Fraud',
  'Other'
] as const;

export type ReportReason = typeof REPORT_REASONS[number];
export type GroupReportReason = typeof GROUP_REPORT_REASONS[number];
export type SessionReportReason = typeof SESSION_REPORT_REASONS[number];

/**
 * Create a report for a user and automatically block them
 */
export async function createUserReport(
  reportedUserId: string,
  reason: string,
  description?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    if (user.id === reportedUserId) {
      return { success: false, error: 'Cannot report yourself' };
    }

    const { data: reportData, error: reportError } = await supabase
      .from('reports')
      .insert({
        reporter_id: user.id,
        reported_user_id: reportedUserId,
        report_type: 'user',
        reason,
        description: description || null,
        status: 'pending'
      })
      .select('id')
      .single();

    if (reportError) {
      logger.error('Error creating user report:', reportError);
      return { success: false, error: reportError.message };
    }

    // Automatically block the reported user
    const blockResult = await blockUser(reportedUserId);
    if (!blockResult.success) {
      logger.warn('Report created but blocking failed:', blockResult.error);
    }

    // Notify admins about the new report
    if (reportData?.id) {
      await notifyAdminsAboutNewReport(reportData.id, 'user', reason);
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in createUserReport:', error);
    return { success: false, error: 'Failed to create report' };
  }
}

/**
 * Create a report for a group
 */
export async function createGroupReport(
  reportedGroupId: string,
  reason: string,
  description?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: reportData, error: reportError } = await supabase
      .from('reports')
      .insert({
        reporter_id: user.id,
        reported_group_id: reportedGroupId,
        report_type: 'group',
        reason,
        description: description || null,
        status: 'pending'
      })
      .select('id')
      .single();

    if (reportError) {
      logger.error('Error creating group report:', reportError);
      return { success: false, error: reportError.message };
    }

    // Notify admins about the new report
    if (reportData?.id) {
      await notifyAdminsAboutNewReport(reportData.id, 'group', reason);
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in createGroupReport:', error);
    return { success: false, error: 'Failed to create report' };
  }
}

/**
 * Create a report for a session
 */
export async function createSessionReport(
  reportedSessionId: string,
  reason: string,
  description?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: reportData, error: reportError } = await supabase
      .from('reports')
      .insert({
        reporter_id: user.id,
        reported_session_id: reportedSessionId,
        report_type: 'session',
        reason,
        description: description || null,
        status: 'pending'
      })
      .select('id')
      .single();

    if (reportError) {
      logger.error('Error creating session report:', reportError);
      return { success: false, error: reportError.message };
    }

    // Notify admins about the new report
    if (reportData?.id) {
      await notifyAdminsAboutNewReport(reportData.id, 'session', reason);
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in createSessionReport:', error);
    return { success: false, error: 'Failed to create report' };
  }
}

/**
 * Get all reports created by the current user
 */
export async function getMyReports(): Promise<{ success: boolean; reports?: Report[]; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('reporter_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching reports:', error);
      return { success: false, error: error.message };
    }

    return { success: true, reports: data || [] };
  } catch (error) {
    logger.error('Error in getMyReports:', error);
    return { success: false, error: 'Failed to fetch reports' };
  }
}

/**
 * Check if user has already reported another user
 */
export async function hasReportedUser(reportedUserId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('reports')
      .select('id')
      .eq('reporter_id', user.id)
      .eq('reported_user_id', reportedUserId)
      .eq('report_type', 'user')
      .single();

    return !!data && !error;
  } catch (error) {
    return false;
  }
}

/**
 * Check if user has already reported a group
 */
export async function hasReportedGroup(reportedGroupId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('reports')
      .select('id')
      .eq('reporter_id', user.id)
      .eq('reported_group_id', reportedGroupId)
      .eq('report_type', 'group')
      .single();

    return !!data && !error;
  } catch (error) {
    return false;
  }
}

/**
 * Check if user has already reported a session
 */
export async function hasReportedSession(reportedSessionId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('reports')
      .select('id')
      .eq('reporter_id', user.id)
      .eq('reported_session_id', reportedSessionId)
      .eq('report_type', 'session')
      .single();

    return !!data && !error;
  } catch (error) {
    return false;
  }
}